import React, { useState, useEffect, useMemo } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, RotateCcw, AlertCircle, Variable, Hash, Calendar, List, Type } from 'lucide-react';
import {
  extractVariables,
  mergeWithDefinitions,
  validateVariables,
  substituteVariables,
} from './variableEngine';

const TYPE_ICONS = { text: Type, number: Hash, date: Calendar, dropdown: List };
const TYPE_COLORS = { text: 'text-blue-400', number: 'text-amber-400', date: 'text-emerald-400', dropdown: 'text-purple-400' };

export default function VariableForm({
  sqlTemplate,
  definitions = [],
  prefilled = {},
  onExecute,
  onCancel,
  compact = false,
  autoExecute = false,
}) {
  const extracted = useMemo(() => extractVariables(sqlTemplate), [sqlTemplate]);
  const specs = useMemo(() => mergeWithDefinitions(extracted, definitions), [extracted, definitions]);

  const [values, setValues] = useState(() => {
    const init = {};
    for (const spec of specs) {
      if (prefilled[spec.name] !== undefined) init[spec.name] = String(prefilled[spec.name]);
      else if (spec.defaultValue !== null && spec.defaultValue !== undefined) init[spec.name] = String(spec.defaultValue);
      else init[spec.name] = '';
    }
    return init;
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Auto-execute if all variables are pre-filled
  useEffect(() => {
    if (!autoExecute || specs.length === 0) return;
    const allFilled = specs.every(s => values[s.name] !== undefined && values[s.name] !== null && values[s.name] !== '');
    if (allFilled) {
      const validation = validateVariables(specs, values);
      if (validation.valid) {
        const { sql, substitutions } = substituteVariables(sqlTemplate, specs, values);
        onExecute(sql, substitutions);
      }
    }
  }, []);

  // No variables → pass through
  useEffect(() => {
    if (specs.length === 0 && onExecute) onExecute(sqlTemplate, {});
  }, [specs.length]);

  if (specs.length === 0) return null;

  const handleChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
    if (errors[name]) setErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  const handleSubmit = () => {
    const validation = validateVariables(specs, values);
    if (!validation.valid) {
      setErrors(validation.errors);
      const allTouched = {};
      specs.forEach(s => { allTouched[s.name] = true; });
      setTouched(allTouched);
      return;
    }
    const { sql, substitutions } = substituteVariables(sqlTemplate, specs, values);
    onExecute(sql, substitutions);
  };

  const handleReset = () => {
    const init = {};
    for (const spec of specs) init[spec.name] = spec.defaultValue !== null ? String(spec.defaultValue) : '';
    setValues(init);
    setErrors({});
    setTouched({});
  };

  const renderField = (spec) => {
    const Icon = TYPE_ICONS[spec.type] || Variable;
    const colorClass = TYPE_COLORS[spec.type] || 'text-slate-400';
    const hasError = touched[spec.name] && errors[spec.name];

    return (
      <div key={spec.name} className={compact ? "flex items-center gap-2" : "space-y-1.5"}>
        <Label htmlFor={`var-${spec.name}`}
          className={`flex items-center gap-1.5 text-xs font-medium ${hasError ? 'text-red-400' : 'text-slate-300'}`}>
          <Icon className={`w-3 h-3 ${colorClass}`} />
          {spec.label}
          {spec.required && <span className="text-red-400">*</span>}
        </Label>

        {spec.type === 'dropdown' && spec.options ? (
          <Select value={values[spec.name] || ''} onValueChange={(val) => handleChange(spec.name, val)}>
            <SelectTrigger id={`var-${spec.name}`}
              className={`h-8 text-xs bg-slate-800/50 border-slate-600 ${hasError ? 'border-red-500' : ''} ${compact ? 'w-32' : 'w-full'}`}>
              <SelectValue placeholder={spec.placeholder || `Select ${spec.label}...`} />
            </SelectTrigger>
            <SelectContent>
              {spec.options.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return <SelectItem key={optValue} value={String(optValue)}>{optLabel}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        ) : spec.type === 'date' ? (
          <Input data-testid="sentinel-variableform-input" id={`var-${spec.name}`} type="date" value={values[spec.name] || ''}
            onChange={(e) => handleChange(spec.name, e.target.value)}
            className={`h-8 text-xs bg-slate-800/50 border-slate-600 ${hasError ? 'border-red-500' : ''} ${compact ? 'w-36' : 'w-full'}`} />
        ) : spec.type === 'number' ? (
          <Input data-testid="sentinel-variableform-input-1" id={`var-${spec.name}`} type="number" value={values[spec.name] || ''}
            onChange={(e) => handleChange(spec.name, e.target.value)}
            placeholder={spec.placeholder || spec.defaultValue || ''}
            min={spec.min ?? undefined} max={spec.max ?? undefined}
            className={`h-8 text-xs bg-slate-800/50 border-slate-600 ${hasError ? 'border-red-500' : ''} ${compact ? 'w-24' : 'w-full'}`} />
        ) : (
          <Input data-testid="sentinel-variableform-input-2" id={`var-${spec.name}`} type="text" value={values[spec.name] || ''}
            onChange={(e) => handleChange(spec.name, e.target.value)}
            placeholder={spec.placeholder || spec.defaultValue || `Enter ${spec.label}...`}
            className={`h-8 text-xs bg-slate-800/50 border-slate-600 ${hasError ? 'border-red-500' : ''} ${compact ? 'w-40' : 'w-full'}`} />
        )}

        {hasError && (
          <p className="text-[10px] text-red-400 flex items-center gap-1 mt-0.5">
            <AlertCircle className="w-3 h-3" />{errors[spec.name]}
          </p>
        )}
      </div>
    );
  };

  if (compact) {
    return (
      <div className="flex items-end gap-2 flex-wrap p-2 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <Variable className="w-4 h-4 text-amber-400 flex-shrink-0 mb-1" />
        {specs.map(renderField)}
        <Button data-testid="sentinel-run-btn" size="sm" onClick={handleSubmit} className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500">
          <Play className="w-3 h-3 mr-1" /> Run
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 px-2 text-xs text-slate-400" data-testid="sentinel-cancel-btn">Cancel</Button>
        )}
      </div>
    );
  }

  return (
    <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700/50 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Variable className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-slate-200">Query Variables</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-600 text-slate-400">
            {specs.length} {specs.length === 1 ? 'variable' : 'variables'}
          </Badge>
        </div>
        <Button data-testid="sentinel-reset-btn" size="sm" variant="ghost" onClick={handleReset}
          className="h-6 px-2 text-[10px] text-slate-500 hover:text-slate-300">
          <RotateCcw className="w-3 h-3 mr-1" /> Reset
        </Button>
      </div>

      <div className={`grid gap-3 ${specs.length === 1 ? 'grid-cols-1' : specs.length === 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3'}`}>
        {specs.map(renderField)}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800">
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 px-3 text-xs text-slate-400" data-testid="sentinel-cancel-btn-1">Cancel</Button>
        )}
        <Button data-testid="sentinel-run-query-btn" size="sm" onClick={handleSubmit} className="h-7 px-4 text-xs bg-emerald-600 hover:bg-emerald-500">
          <Play className="w-3 h-3 mr-1" /> Run Query
        </Button>
      </div>
    </div>
  );
}